import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  Tabs,
  TabsHeader,
  Tab,
  Alert,
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@material-tailwind/react";
import { BiSolidTimeFive } from "react-icons/bi";
import {
  FaCheckCircle,
  FaInfoCircle,
} from "react-icons/fa";
import { IoIosArrowForward } from "react-icons/io";
import { FiCheckCircle, FiChevronLeft, FiChevronRight, FiDownload, FiRefreshCw } from "react-icons/fi";
import useMobileSize from "../hooks/useMobileSize";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { fetchData, PROXY } from "../lib/utils";
import MarkAsComplete from "../components/MarkAsComplete";
import LessonResources from "../components/LessonResources";
import CourseResourceCard from "../components/CourseResourceCard";
import CurrentCourseSkeleton from "../components/skeleton/CurrentCourseSkeleton";
import useAuth from "../hooks/useAuth";
import LessonDiscussion from "../components/LessonDiscussion";
import { toast } from "react-toastify";
import { getLocalizedServerMessage } from "../lib/serverMessages";

const buildVideoStreamUrl = (videoPath) =>
  `${PROXY}/courses/stream?${new URLSearchParams({ p: videoPath }).toString()}`;

const normalizeDownloadName = (value) =>
  `${String(value || "lesson-video")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "") || "lesson-video"}.mp4`;

const isAbortLikeError = (error) =>
  error?.name === "AbortError" ||
  error?.message?.includes?.("BodyStreamBuffer was aborted") ||
  error?.message?.includes?.("aborted");

const CurrentCourse = () => {
  const { i18n, t } = useTranslation();
  const isMobile = useMobileSize();
  const { id } = useParams();

  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const requestedLesson = new URLSearchParams(location.search).get("lesson");
  const focusCommentId = new URLSearchParams(location.search).get("comment");

  const fetcher = (url) => fetchData(url, accessToken);
  const { data, error, mutate, isLoading } = useSWR(
    `${PROXY}/courses/chapters/${id}`,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const playerRefs = useRef([]);
  const downloadControllersRef = useRef(new Map());
  const isMountedRef = useRef(true);
  const [activeTab, setActiveTab] = useState(null);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [isLeavingCourse, setIsLeavingCourse] = useState(false);
  const [downloadStateByChapter, setDownloadStateByChapter] = useState({});
  const [downloadingChapterId, setDownloadingChapterId] = useState(null);
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [videoState, setVideoState] = useState("loading");
  const leaveCourseLabel = t("Leave-course", { defaultValue: "Leave course" });

  const chapters = useMemo(() => data?.chapters || [], [data?.chapters]);
  const courseFiles = useMemo(() => data?.files || [], [data?.files]);
  const initialTab = chapters?.[0]?._id || null;
  const currentTab = activeTab || initialTab;
  const currentChapterIndex = useMemo(
    () => chapters.findIndex(({ _id }) => _id === currentTab),
    [chapters, currentTab],
  );
  const activeChapter = useMemo(
    () => chapters.find(({ _id }) => _id === currentTab) || chapters[0],
    [chapters, currentTab],
  );
  const hasPreviousChapter = currentChapterIndex > 0;
  const hasNextChapter =
    currentChapterIndex >= 0 && currentChapterIndex < chapters.length - 1;
  const activeChapterTitle =
    activeChapter?.title?.[i18n.language] ||
    activeChapter?.title?.en ||
    activeChapter?.title?.pr ||
    activeChapter?.title?.ps ||
    t("Current-lesson", { defaultValue: "Current lesson" });
  const activeVideoSrc = activeChapter?.video
    ? buildVideoStreamUrl(activeChapter.video)
    : "";
  useEffect(() => {
    isMountedRef.current = true;
    const requestControllers = downloadControllersRef.current;

    return () => {
      isMountedRef.current = false;
      // Abort only requests started by this page when it unmounts.
      requestControllers.forEach((controller) => controller.abort());
      requestControllers.clear();
    };
  }, []);

  useEffect(() => {
    if (!initialTab) {
      return;
    }

    const activeTabStillExists = chapters.some(({ _id }) => _id === activeTab);
    if (!activeTab || !activeTabStillExists) {
      setActiveTab(chapters.some(({ _id }) => _id === requestedLesson) ? requestedLesson : initialTab);
    }
  }, [activeTab, chapters, initialTab, requestedLesson]);
  useEffect(() => {
    if (requestedLesson && chapters.some(({ _id }) => _id === requestedLesson)) setActiveTab(requestedLesson);
  }, [requestedLesson, chapters]);

  useEffect(() => {
    localStorage.removeItem("ccid");
  }, []);

  useEffect(() => {
    setVideoState(activeVideoSrc ? "loading" : "idle");
  }, [activeVideoSrc]);

  const handleTabChange = useCallback((newTab) => {
    const activePlayer = playerRefs.current[0];
    if (activePlayer) {
      activePlayer.pause();
    }
    setActiveTab(newTab);
  }, []);

  const handleNavigateChapter = useCallback(
    (direction) => {
      if (currentChapterIndex < 0) {
        return;
      }

      const nextChapter = chapters[currentChapterIndex + direction];
      if (!nextChapter?._id) {
        return;
      }

      handleTabChange(nextChapter._id);
    },
    [chapters, currentChapterIndex, handleTabChange],
  );

  const createManagedController = useCallback((key) => {
    const previousController = downloadControllersRef.current.get(key);
    if (previousController) {
      previousController.abort();
    }

    const controller = new AbortController();
    downloadControllersRef.current.set(key, controller);
    return controller;
  }, []);

  const clearManagedController = useCallback((key, controller) => {
    if (downloadControllersRef.current.get(key) === controller) {
      downloadControllersRef.current.delete(key);
    }
  }, []);

  const handleDownloadVideo = useCallback(async (chapter) => {
    const chapterId = chapter?._id;
    if (!chapterId || !chapter?.video) {
      return;
    }

    const requestKey = `chapter-download:${chapterId}`;
    const controller = createManagedController(requestKey);
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 45000);

    setDownloadStateByChapter((prev) => ({
      ...prev,
      [chapterId]: {
        ...prev[chapterId],
        status: "preparing",
        error: null,
      },
    }));
    setDownloadingChapterId(chapterId);

    try {
      const response = await fetch(
        `${PROXY}/courses/${id}/chapter/${chapterId}/download`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          signal: controller.signal,
        },
      );
      if (!response.ok) {
        throw new Error("Failed to download video.");
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download =
        normalizeDownloadName(
          chapter?.title?.en || chapter?.title?.[i18n.language] || activeChapterTitle,
        ) ||
        normalizeDownloadName(chapter?.title?.en || chapter?.title?.[i18n.language]);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      const nextDownloadCount = Number(response.headers.get("X-Download-Count") || 0);
      mutate(
        (previous) => ({
          ...previous,
          chapters: (previous?.chapters || []).map((item) =>
            item?._id === chapterId
              ? { ...item, downloadCount: nextDownloadCount || Number(item?.downloadCount || 0) + 1 }
              : item,
          ),
        }),
        false,
      );

      toast.success(
        t("Download-started", { defaultValue: "Your download has started." }),
      );
    } catch (error) {
      if (isAbortLikeError(error)) {
        if (process.env.NODE_ENV === "development") {
          console.warn("Lesson video download was aborted during cleanup.");
        }
        return;
      }

      setDownloadStateByChapter((prev) => ({
        ...prev,
        [chapterId]: {
          ...prev[chapterId],
          status: "error",
          error: timedOut
            ? t("Download-timeout", {
                defaultValue: "Download took too long. Please try again.",
              })
            : error?.message || "Failed to download video.",
        },
      }));

      toast.error(
        t("Download-failed", {
          defaultValue: "Download failed. Please try again.",
        }),
      );
    } finally {
      window.clearTimeout(timeoutId);
      clearManagedController(requestKey, controller);
      if (isMountedRef.current) {
        setDownloadingChapterId(null);
        setDownloadStateByChapter((prev) => ({
          ...prev,
          [chapterId]: {
            ...prev[chapterId],
            status: prev[chapterId]?.status === "error" ? "error" : "ready",
          },
        }));
      }
    }
  }, [accessToken, activeChapterTitle, clearManagedController, createManagedController, i18n.language, id, mutate, t]);

  const handleDownloadCourseFile = useCallback(async (file, chapterId = null) => {
    if (!file?._id) {
      return;
    }

    const requestKey = `file-download:${chapterId || "course"}:${file._id}`;
    const controller = createManagedController(requestKey);
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 30000);

    setDownloadingFileId(file._id);

    try {
      const response = await fetch(
        chapterId ? `${PROXY}/courses/${id}/chapter/${chapterId}/files/${file._id}/download` : `${PROXY}/courses/${id}/files/${file._id}/download`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        throw new Error("Failed to download file.");
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = file.originalName || file.title || "course-file";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      const nextDownloadCount = Number(response.headers.get("X-Download-Count") || 0);
      mutate(
        (previous) => ({
          ...previous,
          ...(chapterId ? {
            chapters: (previous?.chapters || []).map((chapter) => chapter._id === chapterId ? {
              ...chapter,
              files: (chapter.files || []).map((item) => item._id === file._id
                ? { ...item, downloadCount: nextDownloadCount || Number(item.downloadCount || 0) + 1 } : item),
            } : chapter),
          } : {
            files: (previous?.files || []).map((item) => item?._id === file._id
              ? { ...item, downloadCount: nextDownloadCount || Number(item?.downloadCount || 0) + 1 } : item),
          }),
        }),
        false,
      );

      toast.success(
        t("Download-started", { defaultValue: "Your download has started." }),
      );
    } catch (error) {
      if (isAbortLikeError(error)) {
        if (process.env.NODE_ENV === "development") {
          console.warn("Course file download was aborted during cleanup.");
        }
        return;
      }

      toast.error(
        timedOut
          ? t("Download-timeout", {
              defaultValue: "Download took too long. Please try again.",
            })
          : t("Download-failed", {
              defaultValue: "Download failed. Please try again.",
            }),
      );
    } finally {
      window.clearTimeout(timeoutId);
      clearManagedController(requestKey, controller);
      if (isMountedRef.current) {
        setDownloadingFileId(null);
      }
    }
  }, [accessToken, clearManagedController, createManagedController, id, mutate, t]);

  const handleRetryDownloadPreparation = useCallback((chapter) => {
    if (!chapter?._id || !chapter?.video) {
      return;
    }

    setDownloadStateByChapter((prev) => ({
      ...prev,
      [chapter._id]: {
        ...prev[chapter._id],
        status: "idle",
        error: null,
      },
    }));
  }, []);

  const handleLeaveCourse = async () => {
    if (!accessToken) {
      toast.error(t("You-must-be-logged-in-to-leave-the-course"));
      return;
    }

    setIsLeavingCourse(true);
    let payload = null;
    try {
      const response = await fetch(`${PROXY}/courses/${id}/unsubscribe`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || "Failed to leave the course");
      }

      const successMessage = getLocalizedServerMessage(
        payload?.message,
        t,
        "Server-You-have-left-the-course",
      );
      toast.success(successMessage);
      setLeaveDialogOpen(false);
      navigate("/dashboard");
    } catch (error) {
      const errorMessage = getLocalizedServerMessage(
        payload?.message || error?.message,
        t,
        "Server-Failed-to-leave-course",
      );
      toast.error(errorMessage);
    } finally {
      setIsLeavingCourse(false);
    }
  };

  if (error) {
    if (isAbortLikeError(error)) {
      return <CurrentCourseSkeleton />;
    }
    return <div>Error: {error.message}</div>;
  }
  if (isLoading) {
    return <CurrentCourseSkeleton />;
  }

  if (data.chapters.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] mx-10">
        <Alert
          icon={<FaInfoCircle />}
          color="amber"
          className="flex items-center justify-center text-center max-w-lg w-full"
        >
          {t("No-Chapter-Added")}
        </Alert>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto rounded-xl dark:bg-gray-800 dark:border-gray-700 px-3 pt-10 pb-6 mt-6 border-tborder-black/10 lg:mt-10">
      <div className="flex flex-col-reverse lg:flex-row justify-start items-start mb-5 gap-2 lg:gap-10">
        <div className="w-full lg:w-72">
          <div className="w-full bg-gray-200 rounded-full dark:bg-gray-700">
            <div
              className="bg-indigo-500 text-xs font-medium text-gray-50 text-center p-0.5 leading-none rounded-full"
              style={{ width: `${data?.completionPercentage.toFixed(0)}%` }}
            >
              {data?.completionPercentage.toFixed(0)}%
            </div>
          </div>
          <h3 className="text-indigo-500 text-sm font-bold">
            {data?.completionPercentage.toFixed(0)}% &nbsp;{" "}
            {t("Completed-Percentage")}
          </h3>
        </div>

        <div className="flex flex-1 items-center justify-between gap-3">
          <div className="flex items-center justify-center gap-2">
            <p className="font-bold text-lg">
              {data?.category?.name?.[i18n.language]}
            </p>
            <IoIosArrowForward />
            <p className="font-bold">
              {data?.category?.subCategories[0]?.[i18n.language]}
            </p>
          </div>
        </div>
      </div>

      <Tabs value={currentTab} onChange={handleTabChange} orientation={`${isMobile ? "horizontal" : "vertical"}`}>
        <TabsHeader className="w-full lg:w-72 mb-2 h-56 lg:h-[83vh] overflow-y-scroll">
          {data?.chapters.length > 0 &&
            data?.chapters?.map(({ _id, title, isCompleted }) => (
              <Tab
                key={_id}
                value={_id}
                className={`flex items-center justify-start gap-2 mb-3 capitalize ${isCompleted && "text-indigo-500"}`}
                onClick={() => handleTabChange(_id)}
              >
                <div className="flex items-center justify-start gap-2 font-bold">
                  {isCompleted ? (
                    <FaCheckCircle size={25} color="#6366F1" />
                  ) : (
                    <BiSolidTimeFive size={25} color="#C7D2FE" />
                  )}
                  <h6 className="text-xs lg:text-sm text-gray-800 text-start">
                    {title?.[i18n.language] || title?.en || title?.pr || title?.ps}
                  </h6>
                </div>
              </Tab>
            ))}
        </TabsHeader>

        <div className="w-full max-w-4xl rounded-2xl border border-gray-200 bg-white/90 p-3 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/80 mx-auto lg:p-4">
          {activeChapter ? (() => {
            const chapterDownloadState = downloadStateByChapter[activeChapter._id];
            const chapterDownloadStatus = chapterDownloadState?.status || (activeChapter.video ? "ready" : "idle");
            const chapterIsPreparing = chapterDownloadStatus === "preparing";
            const chapterIsReady = chapterDownloadStatus === "ready";
            const chapterHasError = chapterDownloadStatus === "error";
            const chapterIsDownloading = downloadingChapterId === activeChapter._id;
            const chapterButtonDisabled = !activeChapter.video || chapterIsDownloading || chapterIsPreparing;
            const chapterButtonLabel = chapterIsDownloading
              ? t("Downloading", { defaultValue: "Downloading..." })
              : chapterIsPreparing
                ? t("Preparing-download", { defaultValue: "Preparing..." })
                : chapterHasError
                  ? t("Retry-download", { defaultValue: "Retry download" })
                  : t("Download", { defaultValue: "Download" });

            return (
              <div className="p-0">
                    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-black shadow-sm dark:border-gray-700">
                      {videoState === "loading" && (
                        <div className="flex min-h-[180px] items-center justify-center text-sm text-white/80">
                          {t("loading")}
                        </div>
                      )}
                      <video
                        key={activeChapter._id}
                        ref={(el) => (playerRefs.current[0] = el)}
                        controls
                        crossOrigin="anonymous"
                        preload="metadata"
                        width="100%"
                        height={`${isMobile ? "auto" : "380px"}`}
                        src={activeVideoSrc || undefined}
                        onLoadStart={() => setVideoState("loading")}
                        onLoadedMetadata={() => setVideoState("ready")}
                        onError={() => setVideoState("error")}
                      />
                    </div>

                    <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                      <button
                        type="button"
                        onClick={() => handleNavigateChapter(-1)}
                        disabled={!hasPreviousChapter}
                        aria-label={t("Previous", { defaultValue: "Previous video" })}
                        className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-200 dark:hover:bg-gray-800 dark:disabled:border-gray-800 dark:disabled:bg-gray-900/30 dark:disabled:text-gray-500 sm:w-auto"
                      >
                        <FiChevronLeft aria-hidden="true" className="text-base" />
                        <span>{t("Previous", { defaultValue: "Previous" })}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleNavigateChapter(1)}
                        disabled={!hasNextChapter}
                        aria-label={t("Next", { defaultValue: "Next video" })}
                        className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-200 dark:hover:bg-gray-800 dark:disabled:border-gray-800 dark:disabled:bg-gray-900/30 dark:disabled:text-gray-500 sm:ml-auto sm:w-auto"
                      >
                        <span>{t("Next", { defaultValue: "Next" })}</span>
                        <FiChevronRight aria-hidden="true" className="text-base" />
                      </button>
                    </div>

                    <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-500">
                            <span className="inline-flex h-2 w-2 rounded-full bg-indigo-500" />
                            {t("Current-lesson", {
                              defaultValue: "Current lesson",
                            })}
                          </div>
                          <h2 className="font-bold text-md text-gray-900 dark:text-white lg:text-xl">
                            {activeChapterTitle}
                          </h2>
                          <div className="text-sm font-medium text-slate-500 dark:text-slate-300">
                            {Number(activeChapter?.downloadCount || 0)} {t("Downloads-count", { defaultValue: "downloads" })}
                          </div>
                          <div
                            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                              chapterHasError
                                ? "bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300"
                                : videoState === "error"
                                  ? "bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300"
                                  : chapterIsReady && videoState !== "loading"
                                  ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300"
                                  : "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300"
                            }`}
                            aria-live="polite"
                          >
                            {chapterHasError || videoState === "error" ? (
                              <FiRefreshCw className="text-sm" />
                            ) : chapterIsReady && videoState !== "loading" ? (
                              <FiCheckCircle className="text-sm" />
                            ) : (
                              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            )}
                            <span>
                              {chapterHasError || videoState === "error"
                                ? t("Download-unavailable", {
                                    defaultValue: "Download unavailable",
                                  })
                                : chapterIsReady && videoState !== "loading"
                                  ? t("Download-ready", {
                                      defaultValue: "Download ready",
                                    })
                                  : t("Preparing-download-message", {
                                      defaultValue:
                                        "Preparing download for this lesson",
                                    })}
                            </span>
                          </div>
                        </div>

                        <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap lg:w-auto lg:justify-end">
                          <button
                            type="button"
                            onClick={
                              chapterHasError
                                ? () =>
                                    handleRetryDownloadPreparation({
                                      _id: activeChapter._id,
                                      video: activeChapter.video,
                                    })
                                : () =>
                                    handleDownloadVideo({
                                      _id: activeChapter._id,
                                      title: activeChapter.title,
                                      video: activeChapter.video,
                                    })
                            }
                            disabled={chapterButtonDisabled && !chapterHasError}
                            aria-label={t("Download-video-file", {
                              defaultValue: "Download lesson video",
                            })}
                            aria-busy={
                              chapterIsPreparing || chapterIsDownloading
                            }
                            className={`inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                              chapterIsReady && !chapterIsDownloading
                                ? "bg-indigo-500 text-white shadow-sm hover:bg-indigo-600"
                                : chapterHasError
                                  ? "border border-red-200 bg-white text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:bg-gray-900 dark:text-red-300"
                                  : "cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                            }`}
                          >
                            {chapterIsPreparing || chapterIsDownloading ? (
                              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : chapterHasError ? (
                              <FiRefreshCw className="text-base" />
                            ) : (
                              <FiDownload className="text-base" />
                            )}
                            {chapterButtonLabel}
                          </button>

                          <MarkAsComplete
                            mutate={mutate}
                            courseId={id}
                            chapterId={activeChapter._id}
                            isCompleted={activeChapter.isCompleted}
                            completedBy={activeChapter.completedBy}
                          />
                        </div>
                      </div>
                    </div>

                    {courseFiles.length > 0 && (
                      <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900/40">
                        <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-500">
                              {t("Course-resources", { defaultValue: "Course resources" })}
                            </div>
                            <h3 className="mt-1 text-base font-bold text-gray-900 dark:text-white">
                              {t("Readable-downloadable-course-files", {
                                defaultValue: "Readable and downloadable course files",
                              })}
                            </h3>
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-300">
                            {courseFiles.length} {t("Resources", { defaultValue: "resources" })}
                          </div>
                        </div>

                        <div className="grid gap-3">
                          {courseFiles.map((file) => (
                            <CourseResourceCard key={file._id} file={file} onDownload={handleDownloadCourseFile} downloadingFileId={downloadingFileId} />
                          ))}
                        </div>
                      </div>
                    )}

                    <LessonResources chapter={activeChapter} language={i18n.language} onDownload={handleDownloadCourseFile} downloadingFileId={downloadingFileId} />
                    <LessonDiscussion courseId={id} lessonId={activeChapter._id} focusCommentId={focusCommentId} assignedTeacherId={data?.instructor} />
              </div>
            );
          })() : (
            <Alert color="amber" icon={<FaInfoCircle />}>
              {t("No-Chapter-Added")}
            </Alert>
          )}
        </div>
      </Tabs>

      <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900/40">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-slate-500 shadow-sm dark:bg-gray-800 dark:text-slate-300">
            <FaInfoCircle className="text-sm" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {t("Manage-enrollment", {
                defaultValue: "Manage Enrollment",
              })}
            </p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t("Manage-enrollment-description", {
                defaultValue:
                  "You are currently enrolled in this course. You can manage your enrollment here.",
              })}
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {t("Need-to-leave-course", {
                  defaultValue: "Need to leave this course?",
                })}
              </p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {t("Leave-course-soft-description", {
                  defaultValue:
                    "Use this option only if you no longer want this course in your active course list.",
                })}
              </p>
            </div>

            <Button
              variant="outlined"
              color="blue-gray"
              size="sm"
              className="min-w-[10rem] border-slate-300 text-slate-700 hover:bg-slate-50"
              onClick={() => setLeaveDialogOpen(true)}
            >
              {leaveCourseLabel}
            </Button>
          </div>
        </div>
      </div>

      <Dialog
        size="sm"
        open={leaveDialogOpen}
        handler={() => setLeaveDialogOpen(false)}
      >
        <DialogHeader className="text-base font-semibold">
          {t("Confirm-Leave-Course", { defaultValue: "Leave this course?" })}
        </DialogHeader>
        <DialogBody divider className="text-sm text-gray-700">
          {t("Leave-course-modal-description", {
            defaultValue:
              "You will lose access to this course from your active courses. Your progress history may still be kept for records.",
          })}
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button
            variant="text"
            color="blue-gray"
            onClick={() => setLeaveDialogOpen(false)}
            disabled={isLeavingCourse}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="gradient"
            color="red"
            onClick={handleLeaveCourse}
            disabled={isLeavingCourse}
          >
            {isLeavingCourse
              ? t("Leaving", { defaultValue: "Leaving..." })
              : t("Yes-Leave-Course", {
                  defaultValue: "Yes, leave course",
                })}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
};

export default CurrentCourse;
